import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  useDisclosure,
  ButtonGroup,
  Image,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Switch,
} from '@chakra-ui/react'
import * as React from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlinePlus } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
import Dropzone from '../../settings/components/Dropzone'
import { getStorageImageUrl } from 'utils/imageUrl'
import OptimizedImage from 'components/image/OptimizedImage'

import {
  useGetCarouselSlides,
  useCreateCarouselSlide,
  useUpdateCarouselSlide,
  useDeleteCarouselSlide,
} from 'queries/carousel.query'

const columnHelper = createColumnHelper()

export default function CarouselTable() {
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')

  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure()

  const [selectedSlide, setSelectedSlide] = React.useState(null)
  const toast = useToast()

  const { data: slides, isLoading, refetch } = useGetCarouselSlides()
  const { mutate: createMutate, isPending: isCreating } = useCreateCarouselSlide()
  const { mutate: updateMutate, isPending: isUpdating } = useUpdateCarouselSlide()
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteCarouselSlide()

  // Form State
  const [formData, setFormData] = React.useState({
    title: '',
    subtitle: '',
    link_url: '',
    order: 0,
    is_active: true,
    is_active: true,
    image: null,
  })
  const [imagePreview, setImagePreview] = React.useState(null)

  React.useEffect(() => {
    if (!isCreateOpen && !isEditOpen) {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview)
        setImagePreview(null)
      }
    }
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview)
      }
    }
  }, [imagePreview, isCreateOpen, isEditOpen])

  const handleFileChange = file => {
    setFormData(prev => ({ ...prev, image: file }))
    if (file) {
      setImagePreview(URL.createObjectURL(file))
    } else {
      setImagePreview(null)
    }
  }

  const resetForm = () => {
    let nextOrder = 1;
    if (slides && slides.length > 0) {
      const maxOrder = Math.max(...slides.map(s => s.order || 0));
      nextOrder = maxOrder + 1;
    }
    setFormData({ order: nextOrder, is_active: true, image: null })
    setImagePreview(null)
  }

  const handleEditOpen = slide => {
    setSelectedSlide(slide)
    setFormData({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      link_url: slide.link_url || '',
      order: slide.order || 0,
      is_active: slide.is_active,
      image: null,
    })
    const baseUrl = process.env.REACT_APP_PUBLIC_BACKEND_URL || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : 'http://localhost:8000')
    const defaultPreview = slide.image_path && slide.image_path.startsWith('http')
      ? slide.image_path
      : `${baseUrl}/storage/${slide.image_path}`
    setImagePreview(defaultPreview)
    onOpenEdit()
  }

  const handleDeleteOpen = slide => {
    setSelectedSlide(slide)
    onOpenDelete()
  }

  const handleCreateSubmit = () => {
    const data = new FormData()
    if (formData.title) data.append('title', formData.title)
    if (formData.subtitle) data.append('subtitle', formData.subtitle)
    if (formData.link_url) data.append('link_url', formData.link_url)
    data.append('order', formData.order)
    data.append('is_active', formData.is_active ? 1 : 0)
    if (formData.image) data.append('image', formData.image)

    createMutate(data, {
      onSuccess: () => {
        toast({ title: 'Slide created.', status: 'success', duration: 3000, isClosable: true })
        onCloseCreate()
        resetForm()
      },
      onError: err => {
        const backendMessage = err.response?.data?.message || err.response?.data?.error || err?.message || 'There was an error creating the slide.'
        toast({
          title: 'Failed to create slide.',
          description: backendMessage,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      }
    })
  }

  const handleEditSubmit = () => {
    const data = new FormData()
    data.append('id', selectedSlide.id)
    data.append('_method', 'PUT')
    if (formData.title) data.append('title', formData.title)
    if (formData.subtitle) data.append('subtitle', formData.subtitle)
    if (formData.link_url) data.append('link_url', formData.link_url)
    data.append('order', formData.order)
    data.append('is_active', formData.is_active ? 1 : 0)
    if (formData.image) data.append('image', formData.image)

    updateMutate(data, {
      onSuccess: () => {
        toast({ title: 'Slide updated.', status: 'success', duration: 3000, isClosable: true })
        onCloseEdit()
        resetForm()
      },
      onError: err => {
        const backendMessage = err.response?.data?.message || err.response?.data?.error || err?.message || 'There was an error updating the slide.'
        toast({
          title: 'Failed to update slide.',
          description: backendMessage,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      }
    })
  }

  const handleDeleteSubmit = () => {
    deleteMutate(selectedSlide.id, {
      onSuccess: () => {
        toast({ title: 'Slide deleted.', status: 'success', duration: 3000, isClosable: true })
        onCloseDelete()
      },
      onError: err => {
        toast({
          title: 'Failed to delete slide.',
          description: err.response?.data?.message || 'There was an error deleting the slide.',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      }
    })
  }

  const columns = React.useMemo(() => [
    columnHelper.accessor('image_path', {
      id: 'image_path',
      header: () => <Text color='gray.400' fontSize='12px'>Image</Text>,
      cell: info => {
        const path = info.getValue()
        const src = getStorageImageUrl(path)
        return <OptimizedImage src={src} alt='Slide' boxSize='50px' objectFit='cover' borderRadius='md' />
      },
    }),
    columnHelper.accessor('order', {
      id: 'order',
      header: () => <Text color='gray.400' fontSize='12px'>Order</Text>,
      cell: info => <Text color={textColor} fontSize='sm'>{info.getValue()}</Text>,
    }),
    columnHelper.accessor('is_active', {
      id: 'is_active',
      header: () => <Text color='gray.400' fontSize='12px'>Active</Text>,
      cell: info => <Text color={textColor} fontSize='sm'>{info.getValue() ? 'Yes' : 'No'}</Text>,
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => <Text color='gray.400' fontSize='12px'>Actions</Text>,
      cell: info => (
        <ButtonGroup size='sm' isAttached variant='outline'>
          <Button onClick={() => handleEditOpen(info.row.original)}>
            <EditIcon color='brand.500' />
          </Button>
          <Button onClick={() => handleDeleteOpen(info.row.original)}>
            <AiOutlineDelete color='red' />
          </Button>
        </ButtonGroup>
      ),
    }),
  ], [textColor])

  const tableData = React.useMemo(() => slides || [], [slides])

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Card direction='column' w='100%' px='0px' overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px='25px' justify='space-between' mb='20px' align='center'>
        <Text color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
          Carousel
        </Text>
        <Button
          leftIcon={<AiOutlinePlus />}
          colorScheme='brand'
          size='sm'
          borderRadius='12px'
          onClick={() => {
            resetForm()
            onOpenCreate()
          }}
        >
          New Slide
        </Button>
      </Flex>
      {isLoading ? (
        <SkeletonTable />
      ) : (
        <Box>
          <Table variant='simple' color='gray.500' mb='24px' mt='12px'>
            <Thead bg={TheadBackground}>
              {table.getHeaderGroups().map(headerGroup => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <Th key={header.id} borderColor={borderColor}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map(row => (
                <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                  {row.getVisibleCells().map(cell => (
                    <Td key={cell.id} borderColor={borderColor}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCloseCreate}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Carousel Slide</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl mb={4}>
              <FormLabel>Image</FormLabel>
              <Dropzone
                w={{ base: '100%', '2xl': '100%' }}
                me='36px'
                maxH={{ base: '60%', lg: '50%', '2xl': '100%' }}
                minH={{ base: '60%', lg: '50%', '2xl': '100%' }}
                title='Upload Photo'
                preview={imagePreview}
                onFileChange={handleFileChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Order</FormLabel>
              <Input type="number" value={formData.order} onChange={e => setFormData({ ...formData, order: e.target.value })} />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0">Active</FormLabel>
              <Switch isChecked={formData.is_active} onChange={e => setFormData({ ...formData, is_active: e.target.checked })} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand" mr={3} onClick={handleCreateSubmit} isLoading={isCreating}>Save</Button>
            <Button onClick={onCloseCreate}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onCloseEdit}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Carousel Slide</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl mb={4}>
              <FormLabel>Image (leave empty to keep current)</FormLabel>
              <Dropzone
                w={{ base: '100%', '2xl': '100%' }}
                me='36px'
                maxH={{ base: '60%', lg: '50%', '2xl': '100%' }}
                minH={{ base: '60%', lg: '50%', '2xl': '100%' }}
                title='Upload New Photo'
                preview={imagePreview}
                onFileChange={handleFileChange}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Order</FormLabel>
              <Input type="number" value={formData.order} onChange={e => setFormData({ ...formData, order: e.target.value })} />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0">Active</FormLabel>
              <Switch isChecked={formData.is_active} onChange={e => setFormData({ ...formData, is_active: e.target.checked })} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand" mr={3} onClick={handleEditSubmit} isLoading={isUpdating}>Save</Button>
            <Button onClick={onCloseEdit}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onCloseDelete}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Slide</ModalHeader>
          <ModalCloseButton />
          <ModalBody>Are you sure you want to delete this slide?</ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={handleDeleteSubmit} isLoading={isDeleting}>Delete</Button>
            <Button onClick={onCloseDelete}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  )
}
